import {
  getDisplayOnlySumInsuredMax,
  getInformationalSumInsuredMax,
} from '@/app/config/products.config';
import { groupLifeService } from '@/services';
import type { CoverageSchemaResponse } from '@/features/quotes/types/group-life/coverage-schema.types';
import { useQuery } from '@tanstack/react-query';

const ONE_HOUR_MS = 60 * 60 * 1000;

export function normalizeGroupLifeCoverageReferences(
  schema: CoverageSchemaResponse
): CoverageSchemaResponse {
  return {
    ...schema,
    coverages: schema.coverages.map(coverage => {
      if (coverage.sumInsuredDefinition !== 'PROPORTIONAL_TO_COVERAGE') {
        return coverage;
      }

      const referenceCoverageCode =
        coverage.referenceCoverageCode ??
        coverage.parentCoverageCode ??
        coverage.ParentCoverageCode;

      return referenceCoverageCode
        ? { ...coverage, referenceCoverageCode }
        : coverage;
    }),
  };
}

/**
 * Injeta o teto APENAS EXIBIDO das coberturas configuradas em
 * `informationalSumInsuredMax` (registry de produtos). Nunca sobrescreve um
 * `sumInsuredMax` vindo da API — esse é trava de verdade e tem precedência.
 */
export function applyInformationalSumInsuredMax(
  schema: CoverageSchemaResponse,
  productCode: string | undefined
): CoverageSchemaResponse {
  const informational = getInformationalSumInsuredMax(productCode);
  if (Object.keys(informational).length === 0) return schema;

  return {
    ...schema,
    coverages: schema.coverages.map(coverage => {
      const display = informational[coverage.code];
      if (display == null || coverage.sumInsuredMax != null) return coverage;
      return { ...coverage, sumInsuredMaxDisplay: display };
    }),
  };
}

/**
 * Rebaixa o `sumInsuredMax` das coberturas listadas em
 * `displayOnlySumInsuredMax` para apenas-exibição: o valor sai do campo que o
 * `coverage-resolver` valida e passa para o `sumInsuredMaxDisplay`, que ele
 * deliberadamente ignora. O teto continua na tela; deixa de travar.
 *
 * Roda DEPOIS do `applyInformationalSumInsuredMax`, senão o teto do registry
 * sobrescreveria o valor que acabou de vir da API — e é o da API que o corretor
 * precisa ver, porque é ele que está no produto.
 */
export function demoteSumInsuredMaxToDisplay(
  schema: CoverageSchemaResponse,
  productCode: string | undefined
): CoverageSchemaResponse {
  const codes = getDisplayOnlySumInsuredMax(productCode);
  if (codes.length === 0) return schema;

  const displayOnly = new Set(codes);

  return {
    ...schema,
    coverages: schema.coverages.map(coverage => {
      if (!displayOnly.has(coverage.code) || coverage.sumInsuredMax == null) {
        return coverage;
      }
      const { sumInsuredMax, ...rest } = coverage;
      return { ...rest, sumInsuredMaxDisplay: sumInsuredMax };
    }),
  };
}

export function useGroupLifeCoverageSchema(params: {
  productCode: string | undefined;
  versionDate?: string;
}) {
  return useQuery<CoverageSchemaResponse>({
    queryKey: [
      'group-life',
      'coverage-schema',
      params.productCode,
      params.versionDate,
    ],
    queryFn: async () => {
      if (!params.productCode) {
        throw new Error('productCode é obrigatório');
      }
      const result = await groupLifeService.getCoveragesSchema({
        productCode: params.productCode,
        versionDate: params.versionDate,
      });
      if (!result.success || !result.data) {
        throw new Error(
          result.error ?? 'Falha ao carregar o catálogo de coberturas'
        );
      }
      return demoteSumInsuredMaxToDisplay(
        applyInformationalSumInsuredMax(
          normalizeGroupLifeCoverageReferences(result.data),
          params.productCode
        ),
        params.productCode
      );
    },
    enabled: !!params.productCode,
    staleTime: ONE_HOUR_MS,
    gcTime: ONE_HOUR_MS,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
